/**
 * Property-scoped assignable members for "Assign to" dropdowns.
 * Returns only ACTIVE, ACCEPTED members with access to this property:
 * - Team admins (property_access grantee_type=user, invite role company_admin/cohost)
 * - Cleaners (cleaner_assignments, company cleaners, or accepted cleaner invitees)
 * Each member: name (First Last or email fallback), role label, optional permission.
 * Used by TurnoverTaskModal (universal task create/edit).
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyAccessAndInvites } from "@/hooks/useMemberInvites";
import { usePropertyCleanerAssignments } from "@/hooks/useCleanerAssignments";
import { usePropertyCleaningAssignment, useCompanyCleanersForProperty } from "@/hooks/useCleaningCompanies";
import { usePropertyCohostAccess } from "@/hooks/useCohostInvites";
import { hasTeamPermission, resolveTeamPermissions } from "@/lib/teamPermissions";

export const USER_PREFIX = "user:";

export interface AssignableMember {
  /** Select value: "user:uuid" for team admin, or cleaner id for cleaner */
  value: string;
  userId: string | null;
  cleanerId: string | null;
  name: string;
  email: string;
  /** From per-property membership/invite: "Team admin" | "Cleaner". Never inferred from profile. */
  role: "Team admin" | "Cleaner";
  /** From public.profiles.role (owner, company_admin, cleaner). Used for Assign to dropdown display. */
  profile_role?: string | null;
  /** Permission label: "Full access" | "Limited access" (team) or "Worker" | "Viewer" (cleaner). */
  permission?: string;
  /** When true, this team admin can open and complete assigned tasks. Only set for Team admin. */
  can_execute_tasks?: boolean;
  /** Profile photo URL when available. */
  avatarUrl?: string | null;
}

/** Map profiles.role to display label for Assign to dropdown. */
export function profileRoleToDisplayLabel(role: string | null | undefined): string {
  if (!role || !role.trim()) return "—";
  const r = role.trim().toLowerCase();
  if (r === "owner") return "Owner";
  if (r === "company_admin") return "Team admin";
  if (r === "cleaner") return "Cleaner";
  return role.trim();
}

/** Format for Assign to dropdown. Uses property membership: Team Member, Cleaner. Never label team as Cleaner. */
export function formatAssigneeLabel(member: AssignableMember): string {
  const roleLabel =
    member.role === "Cleaner" ? "Cleaner" : member.role === "Team admin" ? "Team Member" : "—";
  return `${member.name} (${roleLabel})`;
}

export interface UsePropertyAssignableMembersResult {
  members: AssignableMember[];
  isLoading: boolean;
}

/**
 * Returns assignable members for this property: team admins + cleaners, by name and role.
 * Only accepted/active (property_access rows; no revoked/pending).
 * Name: display_name from profiles, else email prefix or full email.
 */
export function usePropertyAssignableMembers(propertyId: string | undefined): UsePropertyAssignableMembersResult {
  const { user } = useAuth();
  const ownerId = user?.id ?? undefined;
  const { data: accessAndInvites, isLoading: accessLoading } = usePropertyAccessAndInvites(propertyId, ownerId);
  const { data: cohostAccess, isLoading: cohostLoading } = usePropertyCohostAccess(propertyId);
  const { data: cleanerAssignments, isLoading: assignmentsLoading } = usePropertyCleanerAssignments(propertyId);
  const { data: companyAssignment } = usePropertyCleaningAssignment(propertyId);
  const { data: companyCleaners } = useCompanyCleanersForProperty(companyAssignment?.company_id);

  // Company-type members: resolve admin user for "Assign to" (Team admin per property).
  const companyIdsFromMembers = useMemo(() => {
    const members = accessAndInvites?.members ?? [];
    return [...new Set(
      (members as Array<{ grantee_type?: string; grantee_company_id?: string }>)
        .filter((m) => m.grantee_type === "company" && m.grantee_company_id)
        .map((m) => m.grantee_company_id!)
    )];
  }, [accessAndInvites?.members]);

  const { data: companiesWithAdmin, isLoading: companiesLoading } = useQuery({
    queryKey: ["cleaning-companies-assignable", companyIdsFromMembers],
    queryFn: async () => {
      if (companyIdsFromMembers.length === 0) return [];
      const { data, error } = await supabase
        .from("cleaning_companies")
        .select("id, admin_user_id")
        .in("id", companyIdsFromMembers);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; admin_user_id: string | null }>;
    },
    enabled: companyIdsFromMembers.length > 0,
    staleTime: 60_000,
  });

  const companyAdminUserIds = useMemo(
    () => [...new Set((companiesWithAdmin ?? []).map((c) => c.admin_user_id).filter(Boolean))] as string[],
    [companiesWithAdmin]
  );

  const { data: companyAdminProfiles, isLoading: companyAdminProfilesLoading } = useQuery({
    queryKey: ["profiles-assignable-company-admins", companyAdminUserIds],
    queryFn: async () => {
      if (companyAdminUserIds.length === 0) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email, first_name, last_name, role")
        .in("id", companyAdminUserIds);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; full_name: string | null; email: string | null; first_name: string | null; last_name: string | null; role: string | null }>;
    },
    enabled: companyAdminUserIds.length > 0,
    staleTime: 60_000,
  });

  // User IDs that have an accepted cleaner invite for this property (so we show them even if cleaner_assignments is missing)
  const cleanerInviteeUserIds = useMemo(() => {
    const invites = accessAndInvites?.invites ?? [];
    const members = accessAndInvites?.members ?? [];
    const emailToUserId = new Map<string, string>();
    for (const m of members as Array<{ grantee_type?: string; grantee_user_id?: string | null; grantee_email?: string | null }>) {
      if (m.grantee_type === "user" && m.grantee_user_id && m.grantee_email) {
        emailToUserId.set((m.grantee_email ?? "").trim().toLowerCase(), m.grantee_user_id);
      }
    }
    const userIds: string[] = [];
    for (const inv of invites as Array<{ email?: string | null; invite_role?: string | null; accepted_at?: string | null; revoked_at?: string | null }>) {
      if (inv.invite_role !== "cleaner" || !inv.accepted_at || inv.revoked_at) continue;
      const email = (inv.email ?? "").trim().toLowerCase();
      if (!email) continue;
      const uid = emailToUserId.get(email);
      if (uid) userIds.push(uid);
    }
    return [...new Set(userIds)];
  }, [accessAndInvites?.invites, accessAndInvites?.members]);

  const { data: cleanersByUserId } = useQuery({
    queryKey: ["cleaners-by-user-ids", cleanerInviteeUserIds],
    queryFn: async () => {
      if (cleanerInviteeUserIds.length === 0) return [] as Array<{ id: string; name: string | null; user_id: string | null }>;
      const { data, error } = await supabase
        .from("cleaners")
        .select("id, name, user_id")
        .in("user_id", cleanerInviteeUserIds);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; name: string | null; user_id: string | null }>;
    },
    enabled: cleanerInviteeUserIds.length > 0,
    staleTime: 60_000,
  });

  const cleanerUserIdsForProfile = useMemo(() => {
    const ids = new Set<string>();
    const assigned = (cleanerAssignments ?? [])
      .map((a: { cleaner?: { user_id?: string | null } }) => a.cleaner?.user_id)
      .filter(Boolean) as string[];
    assigned.forEach((id) => ids.add(id));
    (cleanersByUserId ?? []).forEach((c: { user_id: string | null }) => {
      if (c.user_id) ids.add(c.user_id);
    });
    (companyCleaners ?? []).forEach((c: { user_id?: string | null }) => {
      if (c.user_id) ids.add(c.user_id);
    });
    return [...ids];
  }, [cleanerAssignments, cleanersByUserId, companyCleaners]);

  const { data: cleanerProfilesRole, isLoading: cleanerProfilesRoleLoading } = useQuery({
    queryKey: ["profiles-assignable-cleaners-role", cleanerUserIdsForProfile],
    queryFn: async () => {
      if (cleanerUserIdsForProfile.length === 0) return [] as Array<{ id: string; role: string | null }>;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, role")
        .in("id", cleanerUserIdsForProfile);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; role: string | null }>;
    },
    enabled: cleanerUserIdsForProfile.length > 0,
    staleTime: 60_000,
  });

  const cleanerUserIdToRole = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of cleanerProfilesRole ?? []) {
      map.set(p.id, p.role ?? null);
    }
    return map;
  }, [cleanerProfilesRole]);

  const avatarUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of accessAndInvites?.members ?? []) {
      if (m.grantee_type === "user" && m.grantee_user_id) ids.add(m.grantee_user_id);
    }
    for (const m of cohostAccess?.members ?? []) {
      if (m.cohost_user_id) ids.add(m.cohost_user_id);
    }
    for (const id of companyAdminUserIds) ids.add(id);
    for (const id of cleanerUserIdsForProfile) ids.add(id);
    return [...ids];
  }, [
    accessAndInvites?.members,
    cohostAccess?.members,
    companyAdminUserIds,
    cleanerUserIdsForProfile,
  ]);

  const { data: avatarRows } = useQuery({
    queryKey: ["profiles-assignable-avatars", avatarUserIds],
    queryFn: async () => {
      if (avatarUserIds.length === 0) return [] as Array<{ id: string; avatar_url: string | null }>;
      const { data, error } = await supabase.rpc("get_visible_profile_avatars", {
        p_user_ids: avatarUserIds,
      });
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; avatar_url: string | null }>;
    },
    enabled: avatarUserIds.length > 0,
    staleTime: 15_000,
  });

  const userIdToAvatarUrl = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of avatarRows ?? []) {
      map.set(p.id, p.avatar_url?.trim() || null);
    }
    return map;
  }, [avatarRows]);

  const result = useMemo((): AssignableMember[] => {
    const members = accessAndInvites?.members ?? [];
    const invites = accessAndInvites?.invites ?? [];
    const userMembers = members.filter((m) => m.grantee_type === "user" && m.grantee_user_id);

    // Merge by userId with precedence: Team admin > Owner > Cleaner. One entry per person.
    const byUserId = new Map<string, AssignableMember>();
    const noUserIdList: AssignableMember[] = []; // cleaners with no user_id

    const noUserIdValues = new Set<string>(); // dedupe cleaners with no user_id by value (cleaner id)
    function upsertByUserId(entry: AssignableMember): void {
      const uid = entry.userId ?? null;
      if (uid) {
        const existing = byUserId.get(uid);
        if (!existing) {
          byUserId.set(uid, entry);
          return;
        }
        if (existing.role === "Cleaner" && entry.role === "Team admin") {
          byUserId.set(uid, entry);
        }
        // else keep existing (higher or equal precedence)
      } else {
        if (!noUserIdValues.has(entry.value)) {
          noUserIdValues.add(entry.value);
          noUserIdList.push(entry);
        }
      }
    }

    // Canonical role per email (must match Access & Team): one role per person. Among accepted invites,
    // prefer company_admin so if someone has both Team admin and Cleaner invites we show Team admin.
    const emailToInviteRole = new Map<string, "cleaner" | "company_admin">();
    const acceptedInvites = (invites as Array<{ email?: string | null; invite_role?: string | null; accepted_at?: string | null; revoked_at?: string | null }>).filter(
      (inv) => inv.accepted_at && !inv.revoked_at
    );
    for (const inv of acceptedInvites) {
      const email = (inv.email ?? "").trim().toLowerCase();
      if (!email) continue;
      const role = inv.invite_role;
      if (role === "company_admin" || role === "cohost") {
        emailToInviteRole.set(email, "company_admin");
      }
    }
    for (const inv of acceptedInvites) {
      const email = (inv.email ?? "").trim().toLowerCase();
      if (!email) continue;
      if (emailToInviteRole.has(email)) continue; // already company_admin
      if (inv.invite_role === "cleaner") {
        emailToInviteRole.set(email, "cleaner");
      }
    }

    // Cleaner ids and user_ids from assignments + company
    const cleanerIds = new Set<string>();
    const cleanerIdToUser = new Map<string, string>();
    const cleanerIdToName = new Map<string, string>();
    const assignedCleaners = (cleanerAssignments ?? [])
      .map((a: { cleaner?: { id: string; name: string; user_id?: string | null } }) => a.cleaner)
      .filter(Boolean) as Array<{ id: string; name: string; user_id?: string | null }>;
    const cleanersFromInvites = (cleanersByUserId ?? []) as Array<{ id: string; name: string | null; user_id: string | null }>;
    for (const c of assignedCleaners) {
      cleanerIds.add(c.id);
      if (c.user_id) cleanerIdToUser.set(c.id, c.user_id);
      cleanerIdToName.set(c.id, c.name?.trim() || "—");
    }
    for (const c of companyCleaners ?? []) {
      const cc = c as { id: string; name?: string };
      cleanerIds.add(cc.id);
      cleanerIdToName.set(cc.id, (cc.name ?? "").trim() || "—");
    }
    for (const c of cleanersFromInvites) {
      cleanerIds.add(c.id);
      if (c.user_id) cleanerIdToUser.set(c.id, c.user_id);
      cleanerIdToName.set(c.id, (c.name ?? "").trim() || "—");
    }

    // Team members: only people who are Team in Settings (module permissions / team invite).
    // Cleaners often have property_access + can_execute_tasks — do NOT treat them as Team.
    const teamMemberUserIds = new Set<string>();
    const teamMemberNamesNormalized = new Set<string>();
    const hasTeamModulePermissions = (raw: unknown): boolean =>
      Array.isArray(raw) && raw.some((p) => typeof p === "string" && p.trim().length > 0);

    for (const m of userMembers) {
      const uid = m.grantee_user_id!;
      const email = (m.grantee_email ?? "").trim().toLowerCase();
      const inviteRole = email ? emailToInviteRole.get(email) : undefined;
      const profileRole = ((m as { role?: string | null }).role ?? "").trim().toLowerCase();
      const isCleanerAccess =
        inviteRole === "cleaner" || profileRole === "cleaner";
      const isTeamAccess =
        !isCleanerAccess &&
        (inviteRole === "company_admin" ||
          hasTeamModulePermissions((m as { permissions?: unknown }).permissions) ||
          profileRole === "company_admin");
      if (!isTeamAccess) continue;

      teamMemberUserIds.add(uid);
      const prof = m as { first_name?: string | null; last_name?: string | null; display_name?: string | null; grantee_email?: string | null };
      const first = prof.first_name?.trim();
      const last = prof.last_name?.trim();
      const name = (first && last ? `${first} ${last}` : first || last || (prof.display_name ?? "").trim() || (prof.grantee_email ?? "").trim() || "—");
      teamMemberNamesNormalized.add(name.trim().toLowerCase());
      const permission: string = m.access_level === "full" ? "Full access" : "Limited access";
      const canExecuteTasks = !!(m as { can_execute_tasks?: boolean }).can_execute_tasks;
      if (!canExecuteTasks) continue;
      upsertByUserId({
        value: `${USER_PREFIX}${uid}`,
        userId: uid,
        cleanerId: null,
        name,
        email: m.grantee_email ?? "",
        role: "Team admin",
        profile_role: (m as { role?: string | null }).role ?? null,
        permission,
        can_execute_tasks: true,
        avatarUrl: userIdToAvatarUrl.get(uid) ?? null,
      });
    }

    // Team admins via company: property_access grantee_type=company → company admin user. Always merge (precedence will replace Cleaner).
    const companyMembers = (members as Array<{ grantee_type?: string; grantee_company_id?: string; access_level?: string; can_execute_tasks?: boolean }>).filter(
      (m) => m.grantee_type === "company" && m.grantee_company_id
    );
    for (const m of companyMembers) {
      const company = (companiesWithAdmin ?? []).find((c) => c.id === m.grantee_company_id);
      const adminUserId = company?.admin_user_id;
      if (!adminUserId) continue;
      if (!m.can_execute_tasks) continue;
      const profile = (companyAdminProfiles ?? []).find((p) => p.id === adminUserId) as { id: string; full_name?: string | null; email?: string | null; first_name?: string | null; last_name?: string | null; role?: string | null } | undefined;
      const first = profile?.first_name?.trim();
      const last = profile?.last_name?.trim();
      const name = (first && last ? `${first} ${last}` : first || last || (profile?.full_name ?? "").trim() || (profile?.email ?? "").trim() || "—");
      const permission: string = m.access_level === "full" ? "Full access" : "Limited access";
      const canExecuteTasks = !!(m as { can_execute_tasks?: boolean }).can_execute_tasks;
      upsertByUserId({
        value: `${USER_PREFIX}${adminUserId}`,
        userId: adminUserId,
        cleanerId: null,
        name,
        email: profile?.email ?? "",
        role: "Team admin",
        profile_role: profile?.role ?? null,
        permission,
        can_execute_tasks: canExecuteTasks,
        avatarUrl: userIdToAvatarUrl.get(adminUserId) ?? null,
      });
    }

    // Slice 22 cohosts with Tasks permission (may not be on property_access yet)
    for (const m of cohostAccess?.members ?? []) {
      const uid = m.cohost_user_id;
      if (!uid) continue;
      const perms = resolveTeamPermissions(m.permissions, m.access_level);
      if (!hasTeamPermission(perms, "tasks")) continue;
      const first = m.first_name?.trim();
      const last = m.last_name?.trim();
      const name =
        (first && last ? `${first} ${last}` : first || last || m.display_name?.trim() || m.grantee_email?.trim() || "—");
      teamMemberUserIds.add(uid);
      teamMemberNamesNormalized.add(name.trim().toLowerCase());
      upsertByUserId({
        value: `${USER_PREFIX}${uid}`,
        userId: uid,
        cleanerId: null,
        name,
        email: m.grantee_email ?? "",
        role: "Team admin",
        profile_role: m.role ?? null,
        permission: "Tasks",
        can_execute_tasks: true,
        avatarUrl: userIdToAvatarUrl.get(uid) ?? null,
      });
    }

    // Cleaners: only people whose role in Teams and Access is Cleaner. Skip anyone who is a Team member.
    const addedCleanerIds = new Set<string>();
    // Invite access_level for cleaners: full = Worker, limited = Viewer (match Access & Team display)
    const emailToCleanerPermission = new Map<string, string>();
    for (const inv of invites as Array<{ email?: string | null; invite_role?: string | null; access_level?: string | null; accepted_at?: string | null; revoked_at?: string | null }>) {
      if (inv.invite_role !== "cleaner" || !inv.accepted_at || inv.revoked_at) continue;
      const email = (inv.email ?? "").trim().toLowerCase();
      if (email) emailToCleanerPermission.set(email, inv.access_level === "full" ? "Worker" : "Viewer");
    }

    const isTeamMemberByUserId = (userId: string | null) => userId != null && teamMemberUserIds.has(userId);
    const isTeamMemberByMember = (member: { grantee_email?: string | null }) => {
      const email = (member.grantee_email ?? "").trim().toLowerCase();
      return email ? emailToInviteRole.get(email) === "company_admin" : false;
    };
    const isTeamMemberName = (cleanerName: string) =>
      teamMemberNamesNormalized.has((cleanerName ?? "").trim().toLowerCase());
    const shouldSkipCleaner = (
      c: { user_id?: string | null; id: string; name?: string | null },
    ): boolean => {
      if (isTeamMemberByUserId(c.user_id ?? null)) return true;
      const memberForCleaner = members.find((m) => m.grantee_type === "user" && m.grantee_user_id === c.user_id);
      if (memberForCleaner && isTeamMemberByMember(memberForCleaner)) return true;
      const name = cleanerIdToName.get(c.id) || (c.name ?? "").trim() || "";
      if (name && isTeamMemberName(name)) return true;
      return false;
    };
    for (const c of assignedCleaners) {
      if (shouldSkipCleaner(c)) continue; // skip if already Team admin by userId
      if (addedCleanerIds.has(c.id)) continue;
      addedCleanerIds.add(c.id);
      const name = cleanerIdToName.get(c.id) || c.name?.trim() || "—";
      const memberForCleaner = members.find((m) => m.grantee_type === "user" && m.grantee_user_id === c.user_id);
      const perm = memberForCleaner?.grantee_email
        ? emailToCleanerPermission.get(memberForCleaner.grantee_email.trim().toLowerCase())
        : undefined;
      const permission: string = perm === "Worker" || perm === "Viewer" ? perm : "—";
      const profileRole = c.user_id ? cleanerUserIdToRole.get(c.user_id) ?? null : null;
      upsertByUserId({
        value: c.id,
        userId: c.user_id ?? null,
        cleanerId: c.id,
        name,
        email: memberForCleaner?.grantee_email ?? "",
        role: "Cleaner",
        profile_role: profileRole,
        permission,
        avatarUrl: c.user_id ? userIdToAvatarUrl.get(c.user_id) ?? null : null,
      });
    }
    for (const c of companyCleaners ?? []) {
      const cc = c as { id: string; name?: string; user_id?: string | null };
      if (shouldSkipCleaner(cc)) continue;
      if (addedCleanerIds.has(cc.id)) continue;
      addedCleanerIds.add(cc.id);
      const profileRole = cc.user_id ? cleanerUserIdToRole.get(cc.user_id) ?? null : null;
      upsertByUserId({
        value: cc.id,
        userId: cc.user_id ?? null,
        cleanerId: cc.id,
        name: (cc.name ?? "").trim() || "—",
        email: "",
        role: "Cleaner",
        profile_role: profileRole,
        permission: "—",
        avatarUrl: cc.user_id ? userIdToAvatarUrl.get(cc.user_id) ?? null : null,
      });
    }
    for (const c of cleanersFromInvites) {
      if (shouldSkipCleaner(c)) continue;
      if (addedCleanerIds.has(c.id)) continue;
      addedCleanerIds.add(c.id);
      const name = cleanerIdToName.get(c.id) || (c.name ?? "").trim() || "—";
      const memberForCleaner = members.find((m) => m.grantee_type === "user" && m.grantee_user_id === c.user_id);
      const perm = memberForCleaner?.grantee_email
        ? emailToCleanerPermission.get(memberForCleaner.grantee_email.trim().toLowerCase())
        : undefined;
      const permission: string = perm === "Worker" || perm === "Viewer" ? perm : "—";
      const profileRole = c.user_id ? cleanerUserIdToRole.get(c.user_id) ?? null : null;
      upsertByUserId({
        value: c.id,
        userId: c.user_id ?? null,
        cleanerId: c.id,
        name,
        email: memberForCleaner?.grantee_email ?? "",
        role: "Cleaner",
        profile_role: profileRole,
        permission,
        avatarUrl: c.user_id ? userIdToAvatarUrl.get(c.user_id) ?? null : null,
      });
    }

    // Fallback: property_access cleaners (profile/invite) who never got a cleaners row in the loops above.
    for (const m of userMembers) {
      const uid = m.grantee_user_id!;
      if (byUserId.has(uid)) continue;
      if (teamMemberUserIds.has(uid)) continue;
      const email = (m.grantee_email ?? "").trim().toLowerCase();
      const inviteRole = email ? emailToInviteRole.get(email) : undefined;
      const profileRole = ((m as { role?: string | null }).role ?? "").trim().toLowerCase();
      if (inviteRole !== "cleaner" && profileRole !== "cleaner") continue;
      const prof = m as { first_name?: string | null; last_name?: string | null; display_name?: string | null };
      const first = prof.first_name?.trim();
      const last = prof.last_name?.trim();
      const name =
        (first && last ? `${first} ${last}` : first || last || (prof.display_name ?? "").trim() || email || "—");
      const cleanerRow = (cleanersByUserId ?? []).find((c) => c.user_id === uid)
        || assignedCleaners.find((c) => c.user_id === uid);
      const cleanerId = cleanerRow?.id ?? null;
      const perm = email ? emailToCleanerPermission.get(email) : undefined;
      upsertByUserId({
        value: cleanerId ?? `${USER_PREFIX}${uid}`,
        userId: uid,
        cleanerId,
        name,
        email: m.grantee_email ?? "",
        role: "Cleaner",
        profile_role: (m as { role?: string | null }).role ?? null,
        permission: perm === "Worker" || perm === "Viewer" ? perm : "—",
        avatarUrl: userIdToAvatarUrl.get(uid) ?? null,
      });
    }

    const list = [...byUserId.values(), ...noUserIdList];
    // Dev check: every assignable member has role from membership (Team admin or Cleaner), never inferred.
    if (process.env.NODE_ENV !== "production") {
      for (const m of list) {
        const ok = m.role === "Team admin" || m.role === "Cleaner";
        if (!ok) console.assert(ok, "Assignable member role must be Team admin or Cleaner", m);
      }
    }
    return list;
  }, [
    accessAndInvites?.members,
    accessAndInvites?.invites,
    cohostAccess?.members,
    cleanerAssignments,
    companyCleaners,
    cleanersByUserId,
    companiesWithAdmin,
    companyAdminProfiles,
    cleanerUserIdToRole,
    userIdToAvatarUrl,
  ]);

  return {
    members: result.filter((m): m is AssignableMember => m != null && (m as AssignableMember).value != null),
    isLoading:
      accessLoading ||
      cohostLoading ||
      assignmentsLoading ||
      companiesLoading ||
      companyAdminProfilesLoading,
  };
}
